/**
 * The patientarmband — an identity band with a Code 128 barcode (PHASE-0.md §2.6).
 *
 * The smaller of the two documents and the one with the higher consequence. A signeringslista
 * that is wrong is read by somebody who can see it is wrong; a band is what a nurse checks
 * against before giving a drug, and during an outage it may be the *only* identification a
 * patient carries, because the band the central EHR printed went on the wrist of someone
 * admitted before the outage started.
 *
 * Three rules follow, and all three are about not letting the machine outrank the eye.
 *
 * - **The digits are always printed, whether or not the barcode is.** The band works with no
 *   scanner at all; the barcode only saves typing. `print:wristband.checkText` states which
 *   of the two wins if they disagree, and it is the digits, because those are what a human
 *   compared against the record.
 * - **An unencodable identifier prints no barcode and says so.** Never an approximation — see
 *   `wristbandBarcode`. A symbol that scans to a different number is a misidentification with
 *   a machine's authority behind it.
 * - **No identifier at all prints no band.** In Sweden a patient who cannot be identified is
 *   given a reservnummer precisely so that there is something to file the record under, so
 *   this is a state to report rather than a layout to fill in.
 *
 * The barcode is drawn here, not fetched: `code128.ts` encodes and this places the one path
 * it produces. The geometry is in module units in the `viewBox` and millimetres on the
 * element, so `MODULE_MM` is the only place the printed X dimension is decided.
 *
 * **Nothing derived or computed goes on the band** — no age, no calculated anything. Name,
 * birth date, the identifier, and where the patient is.
 */

import { formatClinicalDateOnly } from '../format/clinicalDate'
import { guarded, patientName } from '../patient/patientDisplay'
import type { PatientOverview } from '../patient/patientOverviewApi'
import { patientLocation } from '../patient/patientSearchDisplay'
import {
  BARCODE_HEIGHT_MODULES,
  identifierLine,
  wristbandBarcode,
  wristbandIdentifier,
  type PrintTranslate,
} from './printDisplay'

export function Wristband({
  overview,
  t,
}: {
  readonly overview: PatientOverview
  readonly t: PrintTranslate
}) {
  const identifier = wristbandIdentifier(overview)
  const line = identifier === null ? null : identifierLine(identifier, t)
  const location = overview.location
  const birthDate = overview.birthDate

  return (
    <article className="print-document print-wristband">
      <header className="print-document__header">
        <p className="print-document__source">{t('print:document.source')}</p>
        <h1 className="print-document__title">{t('print:wristband.heading')}</h1>
      </header>

      {identifier === null || line === null ? (
        // Not an empty band with a gap where the number goes. A band a ward could fill in
        // with a pen would be a band with an identifier this record cannot recognise.
        <p className="print-wristband__missing">{t('print:wristband.noIdentifier')}</p>
      ) : (
        <>
          <p className="print-wristband__cut">{t('print:wristband.cutHint')}</p>

          {/*
            The strip that is cut out and worn. Its own border, so the cut line is on the
            paper rather than in the instructions.
          */}
          <div className="print-wristband__band">
            {/*
              The identity lines are wrapped rather than sitting loose in the band, and the
              wrapper is load-bearing. The band lays out as three regions — identity, barcode,
              small print — and a region has to be one element to be placed as one. Without
              this, every line was an independent item competing with the barcode for width:
              the birth date and the bed wrapped into single-word columns and the check
              sentence was pushed outside the dashed cut line, which is a sentence printed
              onto the part of the page that gets thrown away.
            */}
            <div className="print-wristband__identity">
              <p className="print-wristband__name">{patientName(overview, t)}</p>

              <p className="print-wristband__identifier">
                <span className="print-wristband__label">{line.label}</span>
                {/* The value verbatim. This is the line the checkText defers to. */}
                <span className="print-wristband__value">{line.value}</span>
              </p>

              {/*
                Born and bed on one line. Both are short, both are things a nurse reads in
                the same glance as the name, and neither deserves a line of its own on a
                strip of paper this size.
              */}
              <p className="print-wristband__meta">
                <span className="print-wristband__fact">
                  <span className="print-wristband__label">{t('print:wristband.born')}</span>
                  {birthDate === null
                    ? t('common:value.notRecorded')
                    : guarded(() => formatClinicalDateOnly(birthDate), t)}
                </span>

                <span className="print-wristband__fact">
                  {patientLocation(
                    { ward: location?.ward ?? null, bed: location?.bed ?? null },
                    t,
                  )}
                </span>
              </p>
            </div>

            <WristbandBarcodeFigure value={identifier.value} t={t} />

            <p className="print-wristband__check">{t('print:wristband.checkText')}</p>
          </div>
        </>
      )}
    </article>
  )
}

/**
 * The barcode, or a stated absence where it would have been.
 *
 * Split out because the two branches are the interesting part of this file and reading them
 * side by side is the point.
 */
function WristbandBarcodeFigure({
  value,
  t,
}: {
  readonly value: string
  readonly t: PrintTranslate
}) {
  const barcode = wristbandBarcode(value)

  if (barcode.kind === 'unencodable') {
    return (
      <p className="print-wristband__barcode-missing">
        {t('print:wristband.barcodeUnavailable')}
      </p>
    )
  }

  return (
    <div className="print-wristband__barcode">
      <svg
        // Hidden from assistive technology on purpose: the value is already on the band as
        // text, immediately above, and a screen reader announcing it twice is noise.
        aria-hidden="true"
        focusable="false"
        // Module units inside, millimetres outside. The `viewBox` includes both quiet
        // zones, so the white space scales with the bars.
        viewBox={`0 0 ${barcode.modules} ${BARCODE_HEIGHT_MODULES}`}
        width={`${barcode.widthMm}mm`}
        height={`${barcode.heightMm}mm`}
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/*
          The quiet zones as drawn content rather than as a background colour. Browsers drop
          background colours when printing unless asked not to; a scanner needs the white
          before the first bar and after the last, and `viewBox` alone does not paint it.
        */}
        <rect x="0" y="0" width={barcode.modules} height={BARCODE_HEIGHT_MODULES} fill="#ffffff" />

        {/*
          Literal black, not a custom property. A theme, a dark mode or an inherited
          `currentColor` must not be able to reduce the contrast a scanner reads — and
          `crispEdges` keeps a bar edge on a device pixel instead of smearing a narrow bar
          into grey across two.
        */}
        <path d={barcode.path} fill="#000000" shapeRendering="crispEdges" />
      </svg>

      {/*
        The human-readable interpretation. Code 128 has no defined HRI of its own, so this is
        simply the value again, under the bars, where a nurse holding a band that will not
        scan can read it.
      */}
      <p className="print-wristband__hri">{value}</p>
    </div>
  )
}
